/**
 * Level Constants
 * Centralized configuration for school level data used across the application
 */

// Tunisia (TN) Level Structure
const LEVEL_STRUCTURE_TN = {
  jardinEnfant: {
    "-2": "niveau.niveauPre",
    "-1": "niveau.niveau0",
  },
  ecole: {
    "-2": "niveau.niveauPre",
    "-1": "niveau.niveau0",
    "1": "niveau.niveau1",
    "2": "niveau.niveau2",
    "3": "niveau.niveau3",
    "4": "niveau.niveau4",
    "5": "niveau.niveau5",
    "6": "niveau.niveau6",
  },
  college: {
    "7": "niveau.niveau7",
    "8": "niveau.niveau8",
    "9": "niveau.niveau9",
  },
  lycee: {
    "10": "niveau.niveau10",
    "11": "niveau.niveau11",
    "12": "niveau.niveau12",
    "13": "niveau.niveau13",
  },
};           
                      
// Kenya (KE) Level Structure
const LEVEL_STRUCTURE_KE = {
  preSchool: {
    "-3": "niveau.playGroup",
    "-2": "niveau.pp1",    
    "-1": "niveau.pp2",    
  },
  prePrimary: {
    "1": "niveau.niveau1",   
    "2": "niveau.niveau2",    
    "3": "niveau.niveau3",    
  },
  middle: {
    "4": "niveau.niveau4",    
    "5": "niveau.niveau5",    
    "6": "niveau.niveau6",    
  },
  junior: {
    "7": "niveau.niveau7",    
    "8": "niveau.niveau8",    
    "9": "niveau.niveau9",    
  },
  senior: {
    "10": "niveau.niveau10",    
    "11": "niveau.niveau11",    
    "12": "niveau.niveau12",    
  },
};

// Default to TN for backward compatibility
const LEVEL_STRUCTURE = LEVEL_STRUCTURE_TN;

// School types for college + lycée (secondary).
export const COLLEGE_LYCEE_SCHOOL_TYPES_TN = ['college', 'lycee'];
// School types for primary (ecole).
export const PRIMARY_SCHOOL_TYPES_TN = ['jardinEnfant', 'ecole'];

export { LEVEL_STRUCTURE, LEVEL_STRUCTURE_TN, LEVEL_STRUCTURE_KE };

/**
 * Get level data for school type(s)
 * @param {Function} $t - Vue i18n translation function
 * @param {Array<string>} schoolType - Array of school types
 * @param {string} countryCode - Country code (TN or KE), defaults to TN
 * @returns {Object} Combined levels directly
 */
export function getLevels($t, schoolType, countryCode = 'TN') {
  if (!Array.isArray(schoolType) || schoolType.length === 0) {
    return {};
  }
  
  // Select the appropriate level structure based on country code
  const levelStructure = countryCode === 'KE' ? LEVEL_STRUCTURE_KE : LEVEL_STRUCTURE_TN;
  
  const combinedLevels = {};
  
  schoolType.forEach(schoolTypeItem => {
    const levels = levelStructure[schoolTypeItem];
    if (levels) {
      Object.entries(levels).forEach(([level, translationKey]) => {
        if (!combinedLevels[level]) {
          // For Kenya, use plain numeric grades (e.g. "1", "2") instead of "1st year", "2nd year", etc.
          if (
            countryCode === 'KE' &&
            typeof translationKey === 'string' &&
            translationKey.startsWith('niveau.niveau') &&
            Number(level) > 0
          ) {
            combinedLevels[level] = String(level);
          } else {
            combinedLevels[level] = $t(translationKey);
          }
        }
      });
    }
  });
  
  return combinedLevels;
}

/**
 * Get ALL level data for a country (regardless of school type)
 * @param {Function} $t - Vue i18n translation function
 * @param {string} countryCode - Country code (TN or KE), defaults to TN
 * @returns {Object} All levels for the country with translated labels
 */
export function getAllLevels($t, countryCode = 'TN') {
  // Select the appropriate level structure based on country code
  const levelStructure = countryCode === 'KE' ? LEVEL_STRUCTURE_KE : LEVEL_STRUCTURE_TN;
  
  const allLevels = {};
  
  // Iterate through all school types and combine all levels
  Object.values(levelStructure).forEach(levels => {
    Object.entries(levels).forEach(([level, translationKey]) => {
      if (!allLevels[level]) {
        // For Kenya, use plain numeric grades (e.g. "1", "2") instead of "1st year", "2nd year", etc.
        if (
          countryCode === 'KE' &&
          typeof translationKey === 'string' &&
          translationKey.startsWith('niveau.niveau') &&
          Number(level) > 0
        ) {
          allLevels[level] = String(level);
        } else {
          allLevels[level] = $t(translationKey);
        }
      }
    });
  });
  
  return allLevels;
}

/**
 * Get the default (first) level for a school type
 * @param {Array<string>} schoolType - Array of school types
 * @param {string} countryCode - Country code (TN or KE), defaults to TN
 * @returns {string} Default level for the school type(s)
 */
export function getDefaultLevel(schoolType, countryCode = 'TN') {
  if (!Array.isArray(schoolType) || schoolType.length === 0) {
    return "1";
  }
  
  // Select the appropriate level structure based on country code
  const levelStructure = countryCode === 'KE' ? LEVEL_STRUCTURE_KE : LEVEL_STRUCTURE_TN;
  
  // Use first school type
  const firstSchoolType = schoolType[0];
  const levels = levelStructure[firstSchoolType];
  if (!levels) return "1";
  
  const firstLevel = Object.keys(levels)[0];
  return firstLevel || "1";
}

/**
 * @param {Function} $t - Vue i18n translation function
 * @param {string} countryCode - Country code (TN or KE), defaults to TN
 * @returns {Object} Levels for college+lycée (TN) or all levels (KE)
 */
export function getLevelsCollegeLycee($t, countryCode = 'TN') {
  if (countryCode === 'KE') return getAllLevels($t, 'KE');
  return getLevels($t, COLLEGE_LYCEE_SCHOOL_TYPES_TN, countryCode);
}

/**
 * @param {Function} $t - Vue i18n translation function
 * @param {string} countryCode - Country code (TN or KE), defaults to TN
 * @returns {Object} Levels for primary (TN) or all levels (KE)
 */
export function getLevelsPrimary($t, countryCode = 'TN') {
  if (countryCode === 'KE') return getAllLevels($t, 'KE');
  return getLevels($t, PRIMARY_SCHOOL_TYPES_TN, countryCode);
}
